/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import * as _ from 'lodash';

import ContentFrame from '../../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../../components/appNav/AppHeaderScrollable';

import Card, { CardSection, EmptyCard } from '../../../components/card/Card';
import { GreenWhiteToggleSwitch } from '../../../components/ToggleSwitch';

import { UserData } from '../../../../models/UserModel';
import { ProfileContentProps } from '../profile/ProfileContentProps';
import { withIntl } from '../../../../utilities/localizedUtil';
import PolicyTextConnected from '../../../components/PolicyText.connected';
import ContentWrapper from '../../../components/ContentWrapper';
import { PolicyInterface, PolicyName, ConsentType, PolicyConsentInterface } from '../../../../models/PolicyModel';
import { TrackEvent } from '../../../../utilities/UserMetrics';
import { ProfileThemeMarkdown } from '../../../components/Markdown';
import CommOptionsLetter from '../../../../design-assets/images/comm-options-letter@2x.png';

interface EditableUserProps extends Partial<Props> {
  userData: UserData;
}

interface EditableUserState {
  userData: UserData;
}

function withEditableUser(Component) {
  return class EditableUser extends React.Component<EditableUserProps, EditableUserState> {
    state: EditableUserState = {
      userData: _.assign({}, this.props.userData),
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
      if (!this.state.userData.id) {
        this.setState({
          userData: _.assign({}, nextProps.userData),
        });
      }
    }

    onChangeUser = (userData, callback = () => {}) => {
      this.setState({ userData: _.assign({}, userData) }, callback);
    }

    render() {
      return (
        <Component
          {...this.props}
          userData={this.state.userData}
          onChangeUser={this.onChangeUser}
          userIsLoaded={!!this.state.userData.id}
        />
      );
    }
  };
}

interface Props extends ProfileContentProps {
  intl: any;
  userData: UserData;
  isSetup: boolean;
  isNewUser: boolean;
  onChangeUser(data: UserData, callback?): void;
  userIsLoaded: boolean;
  policyIds: number[];
  getPolicyByName(name: PolicyName): PolicyInterface;
  updateUser(data, callback): any;
  onAccept(profileId: number, policies: PolicyConsentInterface[], callback): void;
  onFinish(): void;
  onCancel(): void;
}

@withIntl
class CommunicationOptions extends React.Component<Props, {}> {
  onSubmit = (e) => {
    if (e) { e.preventDefault(); }

    const { userData, getPolicyByName, updateUser, onAccept, onFinish, isNewUser } = this.props;
    userData.allow_backpack_email_communication = !!userData.allow_backpack_email_communication;
    userData.allow_thirdparty_email_communication = !!userData.allow_thirdparty_email_communication;
    const { allow_backpack_email_communication, allow_thirdparty_email_communication } = userData;

    const policies = [
      {
        id: getPolicyByName(PolicyName.THIRD_PARTY_EMAIL).id,
        consent_type: allow_thirdparty_email_communication ? ConsentType.PROVIDED : ConsentType.WITHHELD,
      },
      {
        id: getPolicyByName(PolicyName.BACKPACK_EMAIL).id,
        consent_type: allow_backpack_email_communication ? ConsentType.PROVIDED : ConsentType.WITHHELD,
      },
    ];

    updateUser(userData, () => {
      if (isNewUser) {
        TrackEvent.user.setCommunicationOptions();
      }
      onAccept(userData.id, policies, onFinish);
    });
  }

  render() {
    const { userIsLoaded, userData, onChangeUser, intl, onCancel, isSetup } = this.props;
    const { allow_backpack_email_communication = false, allow_thirdparty_email_communication = false } = userData;
    if (!userIsLoaded) { return null; }

    const languageCode = intl.locale;

    return (
      <div>
        <ScrollAwareContext>
          <ScrollAwareAppNavStep
            submitDisabled={false}
            cancel={onCancel}
            submit={this.onSubmit}
            color="white"
          />

          <ContentFrame>
            <ScrollAwareScroller>
              <AppHeaderScrollableText
                id="accountCommsOptions.title.text"
              />
              <ContentWrapper>
                <EmptyCard  style={{ paddingTop: 16 }}>
                  <div className="font-footnote font-color-gsMedDark">
                    <FormattedMessage id="accountCommsOptions.options.leadIn.text" />
                  </div>
                </EmptyCard>

                <Card>
                  <CardSection className="font-color-gsMedDark" hasBorder style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="font-subhead font-color-gsDarkest">
                      <FormattedMessage id="accountCommsOptions.backpackNews.option" />
                    </div>
                    <GreenWhiteToggleSwitch
                      value={!!allow_backpack_email_communication}
                      isOff={!allow_backpack_email_communication}
                      choices={[
                        { name: 'allowed-backpack', value: false },
                        { name: 'disabled-backpack', value: true },
                      ]}
                      onChange={() => {
                        const newUserData = _.assign({}, userData, {
                          allow_backpack_email_communication: !userData.allow_backpack_email_communication,
                          allow_thirdparty_email_communication: false,
                        });
                        onChangeUser(newUserData);
                      }}
                    />
                  </CardSection>
                  <PolicyTextConnected name={PolicyName.BACKPACK_EMAIL} languageCode={languageCode}>
                    {
                      ({ summary }) => (
                        <CardSection hasBorder>
                          <ProfileThemeMarkdown text={summary} />

                          <img width="100%" src={CommOptionsLetter} />
                        </CardSection>
                      )
                    }
                  </PolicyTextConnected>
                </Card>

                {
                  allow_backpack_email_communication && (
                    <Card >
                      <CardSection className="font-color-gsMedDark" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} hasBorder>
                        <div className="font-subhead font-color-gsDarkest">
                          <FormattedMessage id="accountCommsOptions.3rdParty.option" />
                        </div>
                        <GreenWhiteToggleSwitch
                          value={!!allow_thirdparty_email_communication}
                          isOff={!allow_thirdparty_email_communication}
                          choices={[
                            { name: 'allowed-3rdParty', value: false },
                            { name: 'disabled-3rdParty', value: true },
                          ]}
                          onChange={() => {
                            const newUserData = _.assign({}, userData, {
                              allow_thirdparty_email_communication: !userData.allow_thirdparty_email_communication,
                            });
                            onChangeUser(newUserData);
                          }}
                        />
                      </CardSection>

                      <PolicyTextConnected name={PolicyName.THIRD_PARTY_EMAIL} languageCode={languageCode}>
                        {
                          ({ summary }) => (
                            <CardSection hasBorder>
                              <ProfileThemeMarkdown text={summary} />
                            </CardSection>
                          )
                        }
                      </PolicyTextConnected>
                    </Card>
                  )
                }

                <EmptyCard className="font-subhead font-color-gsMedDark">
                  {
                    isSetup && (
                      <p style={{ marginTop: 24 }}>
                        <FormattedMessage id="accountCommsOptions.changeAccountSettings.message.text" />
                      </p>
                    )
                  }

                  <p style={{ marginTop: 24 }}>
                    <FormattedMessage id="accountCommsOptions.systemMessages.message.text" />
                  </p>
                </EmptyCard>
              </ContentWrapper>
            </ScrollAwareScroller>
          </ContentFrame>
        </ScrollAwareContext>
      </div>
    );
  }

}

export default withEditableUser(CommunicationOptions);
